import React, { useEffect } from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import { classes, useSlug } from '@bloomberg/media-ui-core';

import styles from './SearchMobileJapan.module.css';

export type SearchMobileJapanProps = GlobalProps;

/**
 * Override SVG icon styles to match dark theme
 */
const overrideSvgStyles = (container: HTMLElement) => {
  const searchButtons = container.querySelectorAll(
    'button.gsc-search-button-v2, .gsc-search-button button'
  );

  searchButtons.forEach((button) => {
    const svgs = button.querySelectorAll('svg');

    svgs.forEach((svg) => {
      svg.removeAttribute('fill');
      (svg as SVGElement).style.setProperty(
        'fill',
        'var(--color-gray-500)',
        'important'
      );

      const paths = svg.querySelectorAll('path');

      paths.forEach((path) => {
        path.removeAttribute('fill');
        (path as SVGPathElement).style.setProperty(
          'fill',
          'var(--color-gray-500)',
          'important'
        );
      });
    });
  });
};

/**
 * Override clear button (X) styles to white
 */
const overrideButtonStyles = (container: HTMLElement) => {
  const clearButtons = container.querySelectorAll('.gsst_a, .gscb_a');

  clearButtons.forEach((btn) => {
    const buttonElement = btn as HTMLElement;

    buttonElement.style.color = 'var(--color-white)';
  });
};

/**
 * Override input text styles to white for dark theme
 */
const overrideInputStyles = (container: HTMLElement) => {
  const inputs = container.querySelectorAll(
    '.gsc-input input.gsc-input, input.gsc-input, .gsc-input-box input, .gsib_a input'
  );

  inputs.forEach((input) => {
    const style = (input as HTMLElement).style;

    style.cssText += `
      color: var(--color-white) !important;
      -webkit-text-fill-color: var(--color-white) !important;
      caret-color: var(--color-white) !important;
    `;
  });
};

/**
 * Override icon container with filtered overlay to lighten search icon
 * Creates a separate overlay element to avoid filter inheritance affecting input text
 */
const overrideIconContainerStyles = (container: HTMLElement) => {
  const iconContainers = container.querySelectorAll('td.gsib_a');

  iconContainers.forEach((iconContainer) => {
    const td = iconContainer as HTMLElement;
    const bgImage = window.getComputedStyle(td).backgroundImage;
    const bgPosition = window.getComputedStyle(td).backgroundPosition;
    const bgRepeat = window.getComputedStyle(td).backgroundRepeat;

    if (bgImage && bgImage !== 'none' && !td.querySelector('.icon-overlay')) {
      const overlay = document.createElement('div');

      overlay.className = 'icon-overlay';
      overlay.style.cssText = `
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        height: 100% !important;
        background-image: ${bgImage} !important;
        background-repeat: ${bgRepeat} !important;
        background-position: ${bgPosition} !important;
        filter: brightness(0) invert(0.6) !important;
        pointer-events: none !important;
        z-index: 0 !important;
      `;
      td.style.position = 'relative';
      td.style.backgroundImage = 'none';
      td.appendChild(overlay);
    }
  });
};

export const SearchMobileJapan = React.forwardRef<
  HTMLDivElement,
  SearchMobileJapanProps
>(function SearchMobileJapanComponent(props: SearchMobileJapanProps, ref) {
  const { name = 'search-mobile-japan', unstyled } = props;

  const componentName = useSlug(name);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // we will use this ref later for scoping styles that
  // GCSE injects into the page.
  React.useImperativeHandle(ref, () => containerRef.current!);

  // Load Google Custom Search Engine script
  useEffect(() => {
    const script = document.createElement('script');

    script.src = 'https://cse.google.com/cse.js?cx=60d37b10a9757480c';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    const applyStyleOverrides = () => {
      if (!containerRef.current) return;

      // Apply style overrides to injected GCSE elements
      overrideSvgStyles(containerRef.current);
      overrideButtonStyles(containerRef.current);
      overrideInputStyles(containerRef.current);
      overrideIconContainerStyles(containerRef.current);
    };

    // Use MutationObserver to watch for Google CSE rendering
    const observer = new MutationObserver(() => {
      applyStyleOverrides();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Also run on interval as backup
    // const interval = setInterval(applyStyleOverrides, 500);

    return () => {
      observer.disconnect();
      // clearInterval(interval);
    };
  }, []);

  const cx = classes([styles.searchMobileJapanContainer], {
    unstyled,
    name,
  });

  return (
    <div className={cx} ref={containerRef} data-component={componentName}>
      <div className="gcse-search"></div>
    </div>
  );
});
